/* eslint-disable max-len */
import { Button } from "@mui/material";
import React from "react";
import { muiColors } from "themes/colors";

interface CustomButtonProps {
  text: string;
  className?: string;
  disabled?: boolean;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  onClick?: any;
  submitting?: boolean;
  type?: "button" | "submit" | "reset" | undefined;
  variant?: "primary" | "secondary" | "tertiary";
  disableRipple?: boolean;
  sx?: any;
  cyId?: string;
  form?: string;
  guideId?: string;
}

const sharedStyles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "36px",
  fontSize: "14px",
  lineHeight: "16px",
  fontWeight: 500,
  borderRadius: "32px",
  textTransform: "none",
  whiteSpace: "nowrap"
};

export const CustomButton = React.forwardRef<
  HTMLButtonElement,
  CustomButtonProps
>(function CustomButton(props: CustomButtonProps, ref) {
  const {
    text,
    className,
    disabled = false,
    startIcon,
    endIcon,
    onClick,
    submitting,
    type = "button",
    variant = "primary",
    disableRipple,
    sx,
    cyId = "",
    form,
    guideId = ""
  } = props;

  switch (variant) {
    case "secondary":
      return (
        <Button
          className={className || ""}
          disabled={disabled}
          disableRipple={disableRipple}
          ref={ref}
          type={type}
          onClick={onClick}
          data-cy={cyId}
          guide-id={guideId}
          startIcon={
            startIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-2 border-2 border-text-mono-primaryTextColor border-solid rounded-full animate-spin"
              />
            ) : startIcon && !submitting ? (
              startIcon
            ) : undefined
          }
          endIcon={
            endIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-1 border-2 border-text-mono-primaryTextColor border-solid rounded-full animate-spin"
              />
            ) : endIcon && !submitting ? (
              endIcon
            ) : undefined
          }
          sx={{
            ...sharedStyles,
            backgroundColor: muiColors.button.paper,
            color: muiColors.text.primary,
            border: `solid 1px ${muiColors.button.border}`,
            padding: "0px 16px",
            svg: {
              fill: muiColors.text.primary,
              fillOpacity: 1,
              path: {
                fill: muiColors.text.primary,
                fillOpacity: 1
              }
            },
            "&:hover": {
              backgroundColor: muiColors.button.secondaryHover
            },
            "&:disabled": {
              color: muiColors.text.disabled,
              svg: {
                fill: muiColors.text.secondary,
                fillOpacity: 1,
                path: {
                  fill: muiColors.text.secondary,
                  fillOpacity: 1
                }
              }
            },
            ...sx
          }}
        >
          {text}
        </Button>
      );
    case "tertiary":
      return (
        <Button
          className={className || ""}
          disabled={disabled}
          disableRipple={disableRipple}
          ref={ref}
          type={type}
          onClick={onClick}
          data-cy={cyId}
          guide-id={guideId}
          startIcon={
            startIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-2 border-2 border-mono-buttonPrimary border-solid rounded-full animate-spin"
              />
            ) : startIcon && !submitting ? (
              startIcon
            ) : undefined
          }
          endIcon={
            endIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-1 border-2 border-mono-buttonPrimary border-solid rounded-full animate-spin"
              />
            ) : endIcon && !submitting ? (
              endIcon
            ) : undefined
          }
          sx={{
            ...sharedStyles,
            backgroundColor: "transparent",
            color: muiColors.button.primary,
            padding: "0px 8px",
            svg: {
              fill: muiColors.button.primary,
              fillOpacity: 1,
              path: {
                fill: muiColors.button.primary,
                fillOpacity: 1
              }
            },
            "&:hover": {
              backgroundColor: muiColors.button.tertiaryHover
            },
            "&:disabled": {
              color: muiColors.text.disabled,
              svg: {
                fill: muiColors.text.secondary,
                fillOpacity: 1,
                path: {
                  fill: muiColors.text.secondary,
                  fillOpacity: 1
                }
              }
            },
            ...sx
          }}
        >
          {text}
        </Button>
      );
    default:
      return (
        <Button
          form={form}
          className={className || ""}
          disabled={disabled}
          disableRipple={disableRipple}
          ref={ref}
          type={type}
          onClick={onClick}
          data-cy={cyId}
          guide-id={guideId}
          startIcon={
            startIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-2 border-2 border-mono-contrast border-solid rounded-full animate-spin"
              />
            ) : startIcon && !submitting ? (
              startIcon
            ) : undefined
          }
          endIcon={
            endIcon && submitting ? (
              <div
                style={{ borderTopColor: "transparent" }}
                className="w-4 h-4 ml-1 border-2 border-mono-contrast border-solid rounded-full animate-spin"
              />
            ) : endIcon && !submitting ? (
              endIcon
            ) : undefined
          }
          sx={{
            ...sharedStyles,
            backgroundColor: muiColors.button.primary,
            color: muiColors.text.contrast,
            padding: "0px 16px",
            svg: {
              fill: muiColors.text.contrast,
              fillOpacity: 1,
              path: {
                fill: muiColors.text.contrast,
                fillOpacity: 1
              }
            },
            "&:hover": {
              backgroundColor: muiColors.button.primaryDark
            },
            "&:disabled": {
              backgroundColor: muiColors.button.disabled,
              color: muiColors.text.disabled,
              svg: {
                fill: muiColors.text.secondary,
                fillOpacity: 1,
                path: {
                  fill: muiColors.text.secondary,
                  fillOpacity: 1
                }
              }
            },
            ...sx
          }}
        >
          {text}
        </Button>
      );
  }
});
